import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, Renderer2, signal } from '@angular/core';
import { Dictionary } from 'lodash';
import { BehaviorSubject, Subscription, tap } from 'rxjs';
import { Stack } from 'src/app/common/dataStructures/stack';
import { getPlatformLogoPath } from 'src/app/common/platformUtils';
import { beautifyCamelCaseString } from 'src/app/common/stringUtils';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { LabelBindingModel } from 'src/app/models/api/common';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import {
  ExecutionLog,
  PlaybookExecution,
  TriggeredEntitiesTypesApiModel,
  TriggeredEntityPropertiesBase,
  TriggeredResourceProperties,
  TriggeredViolationProperties,
  TriggeredFindingProperties,
  TriggeredIssueProperties,
} from 'src/app/models/api/playbooks/playbookExecution.model';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { PlaybookExecutionsFilters } from 'src/app/models/filters/implementations/pageFilters/playbookExecutionsFilters.model';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { PlaybookExecutionTableColumns } from 'src/app/models/tableColumns/playbookExecutionsTableColumn.model';
import { IPlaybookExecutionViewModel } from 'src/app/models/viewModels/playbookExecutionViewModel';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { PlaybooksExecutionsHttpServiceService } from 'src/app/services/httpServices/playbookExecutionsHttpService/playbook-executions-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { environment } from 'src/environments/environment';
import { buildResourceTypeDisplayName } from '../../../labels/utils';
import { ColumnsOrderService } from 'src/app/services/common/columnsOrder/columns-order.service';
import { TableColumnsNames } from 'src/app/models/tableColumns/tableColumnsNames.enum';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';

@Component({
  selector: 'app-executions-list',
  templateUrl: './executions-list.component.html',
  styleUrls: ['./executions-list.component.scss'],
})
export class ExecutionsListComponent implements OnInit, OnDestroy {
  public columns = signal<ITableColumn[]>([]);
  public pageName = TableColumnsNames.PlaybooksExecutionsHistory;
  public currentPage$ = new BehaviorSubject<IPlaybookExecutionViewModel[]>([]);
  public isLoading$ = new BehaviorSubject(true);
  public showNoData = false;

  public readonly defaultPageSize = this._playbooksExecutionsHttpServiceService.defaultPageSize;
  public totalItems = 0;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;

  public optionsAreSelected = false;
  public pageFilters: PlaybookExecutionsFilters;
  public sorts?: SortColumn[];

  private _paginationNavigation: IPaginationNavigationModel;

  private _filtersOnChangeSubscription$: Subscription;
  private _sortOnChangeSubscription$: Subscription;

  private _firstPageSubscription$: Subscription;
  private _fetchByPageSubscription$: Subscription;

  private readonly _labelNotAvailable = 'common.notAvailable';
  private readonly _labelViolationStatusTooltip = 'playbookExecutions.violationStatusTooltip';
  private readonly _isViolationStatusEnabled: boolean;

  constructor(
    public logger: LoggerService,
    public permissionsService: PermissionsService,
    private readonly _playbooksExecutionsHttpServiceService: PlaybooksExecutionsHttpServiceService,
    private readonly _filtersService: FiltersService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _columnsOrderService: ColumnsOrderService,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _beautifyPipe: BeautifyDisplayStringPipe,
    featureFlagsService: FeatureFlagsService
  ) {
    this._isViolationStatusEnabled = featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.ViolationStatusPlaybookExecHistory
    );
    this._initializePageInfo();
  }

  ngOnInit(): void {
    const availableColumns = this._isViolationStatusEnabled
      ? PlaybookExecutionTableColumns
      : PlaybookExecutionTableColumns.filter((col) => col.propertyKey !== 'violationStatus');

    this.columns.set(this._columnsOrderService.getColumnsOrderFromLocalStorage(this.pageName, availableColumns));

    this.pageFilters = this._filtersService.getFilters(PageWithFiltersEnum.Executions) as PlaybookExecutionsFilters;
    this.pageFilters.restoreSortColumnToDefaultIfNeeded();

    this._subscribeToHandleFiltersOnChange();
    this._subscribeToSortOnChange();

    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._filtersOnChangeSubscription$?.unsubscribe();
    this._sortOnChangeSubscription$?.unsubscribe();
  }

  public onPagination(pagination: PaginationOperationEnum) {
    if (!this.isLoading$.value) {
      if (pagination == PaginationOperationEnum.next) {
        this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
      }

      const pageUrl =
        pagination == PaginationOperationEnum.next
          ? this._paginationNavigation.nextPage
          : this._paginationNavigation.previousPage.pop();
      if (pageUrl) {
        this._fetchPageWhenTokenExist(pageUrl);
      }
    }
  }

  public columnsSelected(selectedColumns: ITableColumn[]) {
    this.columns.set(structuredClone(selectedColumns));
  }

  public onColumnSortClick(sortData: SortColumn) {
    this.pageFilters.sortedColumn$.next(sortData);
  }

  private _subscribeToHandleFiltersOnChange() {
    this._filtersOnChangeSubscription$ = this.pageFilters.onUpdate$.subscribe((selectedFilters) => {
      this.optionsAreSelected = this.pageFilters.getAllSelectedOptions().length > 0;
      this._fetchFirstPage(selectedFilters, this.pageFilters.sortedColumn$.value);
    });
  }

  private _subscribeToSortOnChange() {
    this._sortOnChangeSubscription$ = this.pageFilters.sortedColumn$.subscribe((newSort) => {
      this._fetchFirstPage(this.pageFilters.getSelectedFilters(), newSort);
    });
  }

  private _fetchByPage(continuationToken: string) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._fetchByPageSubscription$ = this._playbooksExecutionsHttpServiceService
      .getExecutionsByContinuationToken(continuationToken)
      .subscribe({
        next: (response) => this._handleExecutionCollectionResponse(response),
        error: (httpError: HttpErrorResponse) => this._fetchRequestOnError(httpError),
      });
  }

  private _fetchFirstPage(filterOptions: ISelectedFilters, sort?: SortColumn) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    if (sort) {
      this.sorts = [sort];
    }

    this._firstPageSubscription$ = this._playbooksExecutionsHttpServiceService
      .getExecutionsFirstPage(filterOptions, this.sorts)
      .subscribe({
        next: (response) => this._handleExecutionCollectionResponse(response),
        error: (httpError: HttpErrorResponse) => this._fetchRequestOnError(httpError),
      });
  }

  private _cancelOngoingHttpRequest() {
    this._firstPageSubscription$?.unsubscribe();
    this._fetchByPageSubscription$?.unsubscribe();
  }

  private _fetchRequestOnError(httpError: HttpErrorResponse) {
    this.currentPage$.next([]);
    this.isLoading$.next(false);
    this.showNoData = true;
    this._httpErrorHandlerService.handle(httpError);
  }

  private _tableBehaviorChangeBeforeFetch() {
    this.currentPage$.next([]);
    this.isLoading$.next(true);
  }

  private _handleExecutionCollectionResponse(response: JsonHalCollection<PlaybookExecution>) {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);

    const items = response._embedded.items;

    this.showNoData = !items || items.length == 0;
    if (!this.showNoData) {
      const viewModelsTemp = items.map((item) => this._transformExecutionResponse(item));
      this.currentPage$.next(viewModelsTemp);
    }

    this.isLoading$.next(false);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    if (!this._paginationNavigation) {
      this._paginationNavigation = {
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel;
    } else {
      this._paginationNavigation.currentPage = links.self.href;
      this._paginationNavigation.nextPage = links.next?.href;
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    this.totalItems = pageInfo.totalItems;
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize);

    if (!this.pageInfo$) {
      this.pageInfo$ = new BehaviorSubject(tablePageInfo);
    } else {
      this.pageInfo$.next(tablePageInfo);
    }
  }

  private _transformExecutionResponse(execution: PlaybookExecution): IPlaybookExecutionViewModel {
    const entity_properties = this._extractPropertiesFromTriggeredEntity(
      execution.integration.id,
      execution.triggeredEntityProperties
    );

    const viewModel: IPlaybookExecutionViewModel = {
      platformIcon: getPlatformLogoPath(execution.platform),
      rowId: execution.id,
      id: execution.id,
      playbook: execution.playbook.name,
      integration: execution.integration.name,
      policy: execution.policy.name,
      trigger: this._formatTriggerDisplayName(execution.playbook.trigger),
      triggeredEntityName: entity_properties.displayName,
      triggeredEntityUrl: entity_properties.url,
      resourceType: entity_properties.resourceType,
      resourceName: entity_properties.resourceName,
      environment: entity_properties.environment,
      createdBy: entity_properties.createdBy ? [entity_properties.createdBy] : ['N/A'],
      ownedBy: entity_properties.ownedBy?.length > 0 ? entity_properties.ownedBy.map((owner) => owner.id) : ['N/A'],
      status: execution.status,
      startTime: execution.startTime,
      endTime: execution.endTime,
      logs: this._getFormattedExecutionLog(execution),
      failedAction: execution.failedAction
        ? execution.failedAction
        : this._labelsProviderPipe.transform(this._labelNotAvailable),
      clickable: false,
      viewLogText: 'View Log',
      viewLogTitle: 'Execution log',
    } as IPlaybookExecutionViewModel;

    if (this._isViolationStatusEnabled) {
      const isViolationEntity =
        execution.triggeredEntityProperties?.entityType === TriggeredEntitiesTypesApiModel.violation;
      viewModel.violationStatus = isViolationEntity
        ? execution.currentViolationStatus || this._labelsProviderPipe.transform(this._labelNotAvailable)
        : this._labelsProviderPipe.transform(this._labelNotAvailable);
      viewModel.violationStatusTooltip = isViolationEntity
        ? undefined
        : this._labelsProviderPipe.transform(this._labelViolationStatusTooltip);
    }

    return viewModel;
  }

  private _getFormattedExecutionLog(execution: PlaybookExecution): string {
    if (execution.executionLog) {
      const entityProperties = this._extractPropertiesFromTriggeredEntity(
        execution.integration.id,
        execution.triggeredEntityProperties
      );
      return JSON.stringify(
        {
          triggeredEntityDetails: entityProperties,
          actions: execution.executionLog.map((log: ExecutionLog) => this._getFormattedExecutionLogActions(log)),
        },
        null,
        '\t'
      );
    }

    const log = execution.log;
    let formattedExecutionLog = '';
    const headerProperties = log[0].split(' | ').slice(1);
    formattedExecutionLog += headerProperties.join('\n') + '\n\n';
    log.slice(1).forEach((actionLog: string, index: number) => {
      const actionLogProperties = actionLog.split(' | ');
      formattedExecutionLog += `#${index + 1}:\n`;
      formattedExecutionLog += actionLogProperties.join('\n') + '\n';
      formattedExecutionLog += '---------------------------------------------------------------------\n';
    });

    return formattedExecutionLog;
  }

  private _getFormattedExecutionLogActions(log: ExecutionLog): {
    action: string;
    index: number;
    status: string;
    additionalData: Dictionary<object>;
    completedAt: Date;
    completionMessage: string;
  } {
    return {
      action: log.action,
      index: log.index,
      status: log.status,
      completedAt: log.completedAt,
      completionMessage: log.completionMessage ?? '',
      additionalData: log.actionInputs,
    };
  }

  private _extractPropertiesFromTriggeredEntity(
    integrationId: string,
    triggeredEntity?: TriggeredEntityPropertiesBase
  ): {
    displayName: string;
    url?: string;
    resourceName: string;
    resourceType: string;
    environment: string;
    createdBy: string;
    ownedBy: LabelBindingModel[];
    ruleId?: string;
    platformResourceId?: string;
  } {
    const emptyResponse = {
      displayName: '',
      url: undefined,
      resourceName: '',
      resourceType: '',
      createdBy: '',
      ownedBy: [],
      environment: '',
    };

    if (!triggeredEntity) {
      return emptyResponse;
    }

    switch (triggeredEntity.entityType) {
      case TriggeredEntitiesTypesApiModel.resource: {
        const triggeredResource: TriggeredResourceProperties = triggeredEntity as TriggeredResourceProperties;
        return {
          displayName: triggeredResource.displayName ? triggeredResource.displayName : '',
          url: `${environment.app_base_url}/app/inventory?integration=${integrationId}&resource=${triggeredResource.id}`,
          resourceName: triggeredResource.displayName ? triggeredResource.displayName : '',
          resourceType: triggeredResource.resourceType
            ? buildResourceTypeDisplayName(triggeredResource.resourceType)
            : '',
          environment: triggeredResource.environment ? triggeredResource.environment : '',
          createdBy: triggeredResource.createdBy ? triggeredResource.createdBy : '',
          ownedBy: this._transformStringsToLabelBindingModel(triggeredResource.ownedBy),
          platformResourceId: triggeredResource.platformResourceId,
        };
      }
      case TriggeredEntitiesTypesApiModel.violation: {
        const triggeredViolation: TriggeredViolationProperties = triggeredEntity as TriggeredViolationProperties;
        return {
          displayName: triggeredViolation.displayName ? triggeredViolation.displayName : '',
          url: `${environment.app_base_url}/app/violations?integration=${integrationId}&violation=${triggeredViolation.id}`,
          resourceName: triggeredViolation.resource.displayName ? triggeredViolation.resource.displayName : '',
          resourceType: triggeredViolation.resource.resourceType
            ? buildResourceTypeDisplayName(triggeredViolation.resource.resourceType)
            : '',
          environment: triggeredViolation.resource.environment ? triggeredViolation.resource.environment : '',
          createdBy: triggeredViolation.resource.createdBy ? triggeredViolation.resource.createdBy : '',
          ownedBy: this._transformStringsToLabelBindingModel(triggeredViolation.resource.ownedBy),
          platformResourceId: triggeredViolation.resource.platformResourceId,
          ruleId: triggeredViolation.ruleId,
        };
      }
      case TriggeredEntitiesTypesApiModel.finding: {
        const triggeredFinding: TriggeredFindingProperties = triggeredEntity as TriggeredFindingProperties;
        return {
          displayName: `${this._beautifyPipe.transform(triggeredFinding.aiService)} - ${triggeredFinding.ruleName}`,
          url: `${environment.app_base_url}/app/findings?integration=${integrationId}&finding=${triggeredFinding.id}`,
          resourceName: '',
          resourceType: this._beautifyPipe.transform(triggeredFinding.stepDiscriminator),
          environment: triggeredFinding.environmentName || '',
          createdBy: triggeredFinding.mainActorEmail || '',
          ownedBy: [],
          ruleId: triggeredFinding.ruleId || '',
        };
      }
      case TriggeredEntitiesTypesApiModel.issue: {
        const triggeredIssue: TriggeredIssueProperties = triggeredEntity as TriggeredIssueProperties;
        return {
          displayName: triggeredIssue.displayName || '',
          url: `${environment.app_base_url}/app/issues?integration=${integrationId}&issue=${triggeredIssue.id}`,
          resourceName: triggeredIssue.primaryResourceName || '',
          resourceType: '',
          environment: '',
          createdBy: '',
          ownedBy: [],
          ruleId: triggeredIssue.ruleId || '',
        };
      }
      default:
        this.logger.error(
          `Failed to extract properties from triggered entity. Unknown entity type: ${triggeredEntity.entityType}`
        );
        return emptyResponse;
    }
  }

  private _transformStringsToLabelBindingModel(labels: string[]): LabelBindingModel[] {
    if (!labels) {
      return [];
    }
    return labels.map((label: string) => ({
      id: label,
      label: {
        id: label,
        displayName: label,
      },
    }));
  }

  private _fetchPageWhenTokenExist(url: string) {
    const continuationToken = new URLSearchParams(url.slice(url.indexOf('?'))).get('continuationToken');
    if (!continuationToken) {
      this.logger.error('Failed to extract continuation token from json hal next inventory resource page link');
      return;
    }

    this._fetchByPage(continuationToken);
  }

  private _formatTriggerDisplayName(triggerType: string): string {
    if (triggerType === TriggerTypes.newFindingFound) {
      return 'New Runtime Finding Found';
    }
    return beautifyCamelCaseString(triggerType);
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 0,
      maxSize: this._playbooksExecutionsHttpServiceService.defaultPageSize,
      totalItems: 0,
      totalPages: 1,
    });
  }
}
